
import { CalendarEvent } from "../types";
import { isSameDay, parseISO } from "date-fns";

/**
 * Identifies guards who have multiple shifts on the same day
 * @param events Array of calendar events to check
 * @returns Map of guard IDs to arrays of event IDs they're assigned to on the same day
 */
export const identifyGuardsWithMultipleShifts = (events: CalendarEvent[]): Map<string, Set<string>> => {
  const guardsWithMultipleShifts = new Map<string, Set<string>>();
  const guardShiftDays = new Map<string, Map<string, Set<string>>>();
  
  // Group shifts by guard and day
  events.forEach(event => {
    const eventDate = parseISO(event.start_date);
    const dateKey = eventDate.toISOString().split('T')[0]; // YYYY-MM-DD
    
    if (event.assigned_guards && event.assigned_guards.length > 0) {
      event.assigned_guards.forEach(guardId => {
        // Initialize nested maps if they don't exist
        if (!guardShiftDays.has(guardId)) {
          guardShiftDays.set(guardId, new Map<string, Set<string>>());
        }
        
        const guardDays = guardShiftDays.get(guardId)!;
        if (!guardDays.has(dateKey)) {
          guardDays.set(dateKey, new Set<string>());
        }
        
        // Add this event to the guard's shifts for this day
        guardDays.get(dateKey)!.add(event.id);
        
        // If the guard now has multiple shifts on this day, add all their shifts to the result
        if (guardDays.get(dateKey)!.size > 1) {
          if (!guardsWithMultipleShifts.has(guardId)) {
            guardsWithMultipleShifts.set(guardId, new Set<string>());
          }
          
          // Add all shifts for this day to the result set
          guardDays.get(dateKey)!.forEach(eventId => {
            guardsWithMultipleShifts.get(guardId)!.add(eventId);
          });
        }
      });
    }
  });
  
  return guardsWithMultipleShifts;
};

/**
 * Checks if an event represents a shift where the assigned guard has multiple shifts on the same day
 * @param event The calendar event to check
 * @param guardsWithMultipleShifts Map of guard IDs to arrays of event IDs they're assigned to
 * @returns True if any guard assigned to this shift has multiple shifts on the same day
 */
export const hasGuardWithMultipleShifts = (
  event: CalendarEvent, 
  guardsWithMultipleShifts: Map<string, Set<string>>
): boolean => {
  if (!event.assigned_guards || event.assigned_guards.length === 0) {
    return false;
  }
  
  return event.assigned_guards.some(guardId => {
    return guardsWithMultipleShifts.has(guardId) && 
           guardsWithMultipleShifts.get(guardId)!.has(event.id);
  });
};
